// Tầng 1 đọc phiếu: OCR TỪNG Ô ngay trên máy (miễn phí), chỉ cho phép đúng ký tự của ô:
// Phần I: A–D · Phần II: Đ/S · Phần III: chữ số, dấu phẩy, dấu trừ.
// Ô đọc chưa chắc (độ tin cậy thấp hoặc sai mẫu) → ghép thành 1 ảnh nhỏ có nhãn để AI đọc (tầng 2).
// Worker RIÊNG (không dùng chung worker của ChalkBridge), chỉ dùng chung file dữ liệu /tesseract.
import { createWorker, PSM } from 'tesseract.js'
import { normalizeCell, type SheetGroup } from './sheetModel'
import type { SheetCell } from './sheetVision'

let workerPromise: ReturnType<typeof createWorker> | null = null
function getWorker(): ReturnType<typeof createWorker> {
  if (!workerPromise) {
    workerPromise = createWorker('eng', 1, {
      workerPath: '/tesseract/worker/worker.min.js',
      corePath: '/tesseract/core',
      langPath: '/tesseract/lang',
      gzip: false,
    })
    workerPromise.catch(() => { workerPromise = null })
  }
  return workerPromise
}

const SETUP: Record<SheetGroup, { psm: PSM; chars: string; minConf: number }> = {
  TN: { psm: PSM.SINGLE_CHAR, chars: 'ABCD', minConf: 70 },
  DS: { psm: PSM.SINGLE_LINE, chars: 'DS', minConf: 70 },
  TL: { psm: PSM.SINGLE_LINE, chars: '0123456789,.-', minConf: 85 },
}

// Ô → ảnh trắng/đen, cắt sát nét chữ, chừa lề — tesseract đọc ký tự rời tốt hơn hẳn.
function prepare(cell: HTMLCanvasElement): HTMLCanvasElement | null {
  const H = 80, W = Math.max(1, Math.round(cell.width * H / cell.height))
  const c = document.createElement('canvas')
  c.width = W; c.height = H
  const ctx = c.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(cell, 0, 0, W, H)
  const img = ctx.getImageData(0, 0, W, H), d = img.data
  const g = new Uint8Array(W * H), hist = new Array(256).fill(0)
  for (let p = 0, i = 0; p < W * H; p++, i += 4) { g[p] = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0; hist[g[p]]++ }
  // Ngưỡng Otsu
  let sum = 0, sumB = 0, wB = 0, best = 0, t = 128
  for (let i = 0; i < 256; i++) sum += i * hist[i]
  for (let i = 0; i < 256; i++) {
    wB += hist[i]; if (!wB) continue
    const wF = W * H - wB; if (!wF) break
    sumB += i * hist[i]
    const between = wB * wF * (sumB / wB - (sum - sumB) / wF) ** 2
    if (between > best) { best = between; t = i }
  }
  let x0 = W, y0 = H, x1 = -1, y1 = -1
  for (let y = 2; y < H - 2; y++) for (let x = 2; x < W - 2; x++) {
    if (g[y * W + x] <= t && g[y * W + x] < 170) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y }
  }
  if (x1 < 0) return null
  const pad = 14, bw = x1 - x0 + 1, bh = y1 - y0 + 1, s = 56 / bh
  const out = document.createElement('canvas')
  out.width = Math.round(bw * s) + 2 * pad; out.height = 56 + 2 * pad
  const o = out.getContext('2d')!
  o.fillStyle = '#fff'; o.fillRect(0, 0, out.width, out.height)
  for (let p = 0, i = 0; p < W * H; p++, i += 4) { const v = g[p] <= t && g[p] < 170 ? 0 : 255; d[i] = d[i + 1] = d[i + 2] = v }
  ctx.putImageData(img, 0, 0)
  o.drawImage(c, x0, y0, bw, bh, pad, pad, bw * s, 56)
  return out
}

export interface CellReading { group: SheetGroup; index: number; value: string; sure: boolean; blank: boolean }

/** OCR các ô (gom theo phần để đổi tham số ít lần). Ô trống → value '' sure=true. */
export async function ocrCells(cells: SheetCell[], onProgress?: (done: number, total: number) => void): Promise<CellReading[]> {
  const worker = await getWorker()
  const out: CellReading[] = []
  let done = 0
  for (const group of ['TN', 'DS', 'TL'] as SheetGroup[]) {
    const mine = cells.filter((c) => c.group === group)
    if (!mine.length) continue
    const cfg = SETUP[group]
    await worker.setParameters({ tessedit_pageseg_mode: cfg.psm, tessedit_char_whitelist: cfg.chars })
    for (const cell of mine) {
      const img = cell.blank ? null : prepare(cell.canvas)
      if (!img) { out.push({ group, index: cell.index, value: '', sure: true, blank: true }); onProgress?.(++done, cells.length); continue }
      const { data } = await worker.recognize(img)
      const raw = (data.text || '').replace(/\s+/g, '')
      const norm = normalizeCell(group, raw)
      out.push({ group, index: cell.index, value: norm.value, sure: !!norm.value && norm.ok && (data.confidence ?? 0) >= cfg.minConf, blank: false })
      onProgress?.(++done, cells.length)
    }
  }
  return out
}

/** Ghép các ô thành ảnh 512×512 (4×4 ô, mỗi ô có nhãn đỏ "I-3" phía trên) — đúng cỡ chế độ "low" của AI
 *  (rẻ nhất, ~85 token/ảnh). Mỗi ô được kéo giãn tương phản → chữ mực nhạt trên ảnh xám vẫn rõ. */
export async function buildMosaics(items: { label: string; canvas: HTMLCanvasElement }[],
  opts: { tile?: number; cols?: number } = {}): Promise<Blob[]> {
  const T = opts.tile ?? 128, LH = 18, COLS = opts.cols ?? 4, PER = COLS * COLS
  const out: Blob[] = []
  for (let start = 0; start < items.length; start += PER) {
    const chunk = items.slice(start, start + PER)
    const rows = Math.ceil(chunk.length / COLS)
    const c = document.createElement('canvas')
    c.width = COLS * T; c.height = rows * T
    const ctx = c.getContext('2d', { willReadFrequently: true })!
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height)
    chunk.forEach((it, i) => {
      const x = (i % COLS) * T, y = Math.floor(i / COLS) * T
      const bw = T - 6, bh = T - LH - 4
      const s = Math.min(bw / it.canvas.width, bh / it.canvas.height)
      const dw = Math.max(1, Math.round(it.canvas.width * s)), dh = Math.max(1, Math.round(it.canvas.height * s))
      const dx = x + Math.round((T - dw) / 2), dy = y + LH + Math.round((bh - dh) / 2)
      ctx.drawImage(it.canvas, dx, dy, dw, dh)
      // kéo giãn tương phản (phân vị 2% → đen, 90% → trắng), chuyển xám
      const img = ctx.getImageData(dx, dy, dw, dh), d = img.data
      const lum: number[] = []
      for (let k = 0; k < d.length; k += 4) lum.push(0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2])
      const sorted = [...lum].sort((a, b) => a - b)
      const lo = sorted[Math.floor(sorted.length * 0.02)], hi = sorted[Math.floor(sorted.length * 0.9)]
      const span = Math.max(30, hi - lo)
      for (let k = 0, j = 0; k < d.length; k += 4, j++) {
        const v = Math.max(0, Math.min(255, ((lum[j] - lo) / span) * 255))
        d[k] = d[k + 1] = d[k + 2] = v
      }
      ctx.putImageData(img, dx, dy)
      ctx.fillStyle = '#c00'; ctx.font = 'bold 15px sans-serif'; ctx.fillText(it.label, x + 5, y + 14)
      ctx.strokeStyle = '#bbb'; ctx.strokeRect(x + 0.5, y + 0.5, T - 1, T - 1)
    })
    out.push(await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('mosaic'))), 'image/jpeg', 0.9)))
  }
  return out
}
